import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DEFAULT_SPLASH, DEFAULT_BRANDING, buildBgStyle, readCachedSplash, readCachedBranding } from '@/lib/theme';
import { X } from 'lucide-react';

const prefersReduced = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ALIGN = { left: 'flex-start', center: 'center', right: 'flex-end' };
const TEXT_ALIGN = { left: 'left', center: 'center', right: 'right' };

// Presentational splash overlay. Reused by the opening gate and the editor's
// full-screen preview. Purely visual — never touches app data/logic.
export function SplashScreen({ config, branding, onDone, preview = false }) {
  const s = { ...DEFAULT_SPLASH, ...(config || {}) };
  const b = { ...DEFAULT_BRANDING, ...(branding || {}) };
  const reduced = prefersReduced();
  const dur = reduced ? 0.4 : Math.min(Math.max(Number(s.animSpeed) || 1.4, 0.5), 3);
  const [exiting, setExiting] = useState(false);
  const doneRef = useRef(false);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    setExiting(true);
    const t = s.transition === 'none' || reduced ? 0 : 400;
    setTimeout(() => onDone && onDone(), t);
  }, [onDone, s.transition, reduced]);

  useEffect(() => {
    const hold = dur * 1000 + (reduced ? 100 : 250);
    const timer = setTimeout(finish, hold);
    return () => clearTimeout(timer);
  }, [dur, finish, reduced]);

  const bg = buildBgStyle(s, false);
  const animClass = reduced || s.animStyle === 'none' ? 'sc-anim-fade' : `sc-anim-${s.animStyle}`;
  const exitClass = exiting
    ? (s.transition === 'slide' ? 'sc-out-slide' : s.transition === 'zoom' ? 'sc-out-zoom' : s.transition === 'none' ? '' : 'sc-out-fade')
    : '';
  const logoAnimClass = s.logoAnim === 'pulse' ? 'sc-logoanim-pulse' : s.logoAnim === 'reveal' ? 'sc-logoanim-reveal' : '';

  return (
    <div
      role="dialog" aria-label="Opening screen" data-testid={preview ? 'splash-preview-fullscreen' : 'splash-screen'}
      onClick={preview ? undefined : finish}
      className={`fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden ${exitClass}`}
      style={{ ...bg, cursor: preview ? 'default' : 'pointer' }}
    >
      {s.overlay && (
        <div className="absolute inset-0" style={{ backgroundColor: s.overlayColor, opacity: s.overlayOpacity }} />
      )}
      <div
        className={`sc-splash-content relative flex w-full max-w-[90vw] flex-col ${animClass} ${s.animStyle === 'logo-reveal' ? 'sc-anim-logo-reveal' : ''}`}
        style={{ ['--sc-dur']: `${dur}s`, alignItems: ALIGN[s.logoAlign] || 'center', textAlign: TEXT_ALIGN[s.logoAlign] || 'center', padding: '24px' }}
      >
        {b.logo && (
          <img src={b.logo} alt="Logo" data-testid="splash-logo"
            className={`sc-splash-logo object-contain ${logoAnimClass}`}
            style={{ height: `${s.logoSize}px`, maxWidth: '80vw', marginBottom: 18 }} />
        )}
        <div className="sc-splash-text">
          {s.showName && (
            <div data-testid="splash-name" style={{ color: s.nameColor, fontSize: `clamp(20px, 6vw, ${s.nameSize}px)`, fontWeight: s.nameWeight, letterSpacing: `${s.nameSpacing}px`, fontFamily: 'var(--font-display, inherit)', lineHeight: 1.15 }}>
              {s.name}
            </div>
          )}
          {s.showTagline && (
            <div data-testid="splash-tagline" style={{ color: s.tagColor, fontSize: `clamp(12px, 3.5vw, ${s.tagSize}px)`, fontWeight: s.tagWeight, letterSpacing: `${s.tagSpacing}px`, marginTop: 8 }}>
              {s.tagline}
            </div>
          )}
        </div>
        {/* Accent loading bar (uses the owner's accent colour) */}
        <div style={{ marginTop: 26, height: 3, width: 120, borderRadius: 9999, overflow: 'hidden', background: 'rgba(255,255,255,0.14)' }}>
          <div style={{ height: '100%', width: '40%', borderRadius: 9999, background: s.accent, animation: reduced ? 'none' : 'sc-slide-up 0s', transform: 'translateX(0)', ...(reduced ? {} : { animation: `scbar ${dur}s ease-in-out infinite` }) }} />
        </div>
      </div>
      {preview && (
        <button data-testid="splash-preview-close" onClick={onDone}
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur hover:bg-black/60">
          <X className="h-5 w-5" />
        </button>
      )}
      <style>{`@keyframes scbar{0%{transform:translateX(-120%)}100%{transform:translateX(320%)}}`}</style>
    </div>
  );
}

// Decides whether to show the opening splash — ONLY once per page load / refresh.
// Route navigations do not remount this, so the splash won't reappear.
let SPLASH_SHOWN = false;
export function SplashGate() {
  const cached = readCachedSplash();
  const cfg = { ...DEFAULT_SPLASH, ...(cached || {}) };
  const [show, setShow] = useState(() => {
    if (SPLASH_SHOWN) return false;
    SPLASH_SHOWN = true;
    return !!cfg.enabled;
  });
  if (!show) return null;
  return <SplashScreen config={cfg} branding={readCachedBranding() || {}} onDone={() => setShow(false)} />;
}
